/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, ReactElement, useState, useEffect } from 'react'
import { Column } from 'react-table'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faTrashAlt, faClone } from '@fortawesome/free-regular-svg-icons'
import { faArrowsAlt } from '@fortawesome/free-solid-svg-icons'
import { FolderFilled, FolderAddOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Modal, Tooltip } from 'antd'
import { DecisionFolderPolicyKey } from '@vms/vmspro3-core/dist/types'
import {
  deleteDecision,
  CreateDecisionAction,
  CreateDecisionFolderAction,
  UpdateDecisionFolderAction,
  UpdateDecisionAction,
  DeleteDecisionFolderAction,
  DeleteDecisionAction,
  MoveDecisionAction,
  ReloadDecisionAction,
} from '@vms/vmspro3-core/dist/actions/decision'

import { DecisionNodeCreateModal } from './DecisionNodeCreateModal'
import { DecisionEditModal } from './DecisionEditModal'
import { DecisionFolderDeleteModal } from './DecisionFolderDeleteModal'
import { DecisionFolderEditModal } from './DecisionFolderEditModal'
import { Table, TableCellRenderer } from '../../common/Table'

import { useAppDispatch } from '../../../redux'
import { useModalState } from '../../../hooks/useModalState'
import { useActionObserverByType } from '../../../hooks/useActionObserver'
import { testId } from '../../../test-automation'
import { CreateDecisionModal } from './CreateDecisionModal'
import { DuplicateDecisionModal } from './DuplicateDecisionModal'
import { MoveDecisionModal } from './MoveDecisionModal'
import { useAccount } from '../../../context'
import { SystemPolicyId } from '@vms/vmspro3-core/dist/systemConsts'
import type { UseDecisionFolder2Result } from '../../../hooks/useDecisionFolder2'
import { DecisionLite } from '../../../types/decisionTypes'
import { joinAncestry } from '@vms/vmspro3-core/dist/utils/ancestry'
import Server from '../../../server/VMSProServerAdapter'
import { gql } from 'graphql-tag'

const SINGLE_DECISION_QUERY = gql`
  query SingleDecision($accountId: ID!, $decisionNodeId: ID!) {
    decisionNode2(accountId: $accountId, decisionNodeId: $decisionNodeId, ancestors: false, children: false) {
      decisionNode {
        id
        name
        entityType
        description {
          value
        }
        optionsCount
        decisionType
      }
    }
  }
`

async function fetchDecisionLite(accountId: string, decisionId: string): Promise<DecisionLite | null> {
  try {
    const response = await Server.graphql({
      query: SINGLE_DECISION_QUERY,
      variables: {
        accountId,
        decisionNodeId: decisionId,
      },
    })

    const { decisionNode } = response.data.decisionNode2

    if (decisionNode?.entityType === 'Decision') {
      return {
        id: decisionNode.id,
        entityType: 'Decision',
        name: decisionNode.name,
        optionsCount: decisionNode.optionsCount,
        description: decisionNode.description,
        decisionType: decisionNode.decisionType,
      }
    }

    return null
  } catch (error) {
    console.error('Failed to fetch decision lite data:', error)
    return null
  }
}

function useDecisionAccessLevel(directoryData: UseDecisionFolder2Result): {
  accessLevel: DecisionFolderPolicyKey
} {
  const { accountUserRecord } = useAccount()
  if (accountUserRecord.policyIds.includes(SystemPolicyId.DECISION_ADMIN)) return { accessLevel: 'Administrator' }
  for (const a of directoryData.ancestors.reverse()) {
    const p = a.policies.find(p => p.userId === accountUserRecord.id)
    if (p && p.policyKey !== 'Inherit') return { accessLevel: p.policyKey as DecisionFolderPolicyKey }
  }
  return { accessLevel: 'None' }
}

type DecisionFolderProps = {
  accountCommonId: string
  decisionFolderId: string
  directoryData: UseDecisionFolder2Result
  refetch?: () => void
}

export function DecisionFolderChildren({
  accountCommonId,
  decisionFolderId,
  directoryData,
  refetch,
}: DecisionFolderProps): ReactElement {
  const childAncestry = joinAncestry(...directoryData.ancestors.map(a => a.id), directoryData.folder.id)
  const { accessLevel } = useDecisionAccessLevel(directoryData)
  const { accountId } = useAccount()

  // Manage local state for children
  const [localChildren, setLocalChildren] = useState(directoryData.children)

  // Update local state when directoryData changes
  useEffect(() => {
    setLocalChildren(directoryData.children)
  }, [directoryData.children])

  // Handle Decision module actions
  useActionObserverByType<
    | CreateDecisionAction
    | CreateDecisionFolderAction
    | UpdateDecisionFolderAction
    | UpdateDecisionAction
    | DeleteDecisionFolderAction
    | DeleteDecisionAction
    | MoveDecisionAction
    | ReloadDecisionAction
  >({
    actionType: [
      'Create Decision',
      'Create Folder',
      'Update Folder',
      'Update Decision',
      'Delete Folder',
      'Delete Decision',
      'Move Decision',
      'Reload Decision',
    ],
    handler: useCallback(
      action => {
        switch (action.type) {
          case 'Create Decision': {
            // Add new decision if it was created in this folder
            if (action.meta.ancestry === childAncestry) {
              const newDecision = {
                id: action.payload.id,
                entityType: 'Decision' as const,
                name: action.payload.name,
                optionsCount: 0,
                description: action.payload.description,
                decisionType: action.payload.type,
              }
              setLocalChildren(prev => [...prev, newDecision])
            }
            break
          }

          case 'Create Folder': {
            // Add new folder if it was created in this folder
            if (action.meta.ancestry === childAncestry) {
              const newFolder = {
                id: action.payload.id,
                entityType: 'DecisionFolder' as const,
                name: action.payload.name,
                description: undefined,
                optionsCount: 0,
                policies: [],
                decisionType: null,
              }
              setLocalChildren(prev => [...prev, newFolder])
            }
            break
          }

          case 'Update Folder': {
            // Update folder if it's in this folder's children
            const { decisionFolderId: updatedFolderId } = action.meta
            setLocalChildren(prev =>
              prev.map(child =>
                child.id === updatedFolderId && child.entityType === 'DecisionFolder'
                  ? { ...child, ...action.payload }
                  : child
              )
            )
            break
          }

          case 'Update Decision': {
            // Update decision if it's in this folder's children
            const { decisionId: updatedDecisionId } = action.meta
            setLocalChildren(prev =>
              prev.map(child =>
                child.id === updatedDecisionId && child.entityType === 'Decision'
                  ? { ...child, ...action.payload }
                  : child
              )
            )
            break
          }

          case 'Delete Folder': {
            // Remove folder if it's in this folder's children
            const { decisionFolderId: deletedFolderId } = action.meta
            setLocalChildren(prev => prev.filter(child => child.id !== deletedFolderId))
            break
          }

          case 'Delete Decision': {
            // Remove decision if it's in this folder
            setLocalChildren(prev => prev.filter(child => child.id !== action.meta.decisionId))
            break
          }

          case 'Move Decision': {
            const { decisionId, dstFolderId } = action.payload

            // Remove decision if it was moved out of this folder
            if (dstFolderId !== decisionFolderId) {
              setLocalChildren(prev => prev.filter(child => child.id !== decisionId))
            }

            // Add decision if it was moved into this folder
            if (dstFolderId === decisionFolderId) {
              // We need to find the decision info - it should be in the action or we may need to construct it
              // For now, we'll trigger a refetch since we don't have full decision data
              if (refetch) {
                refetch()
              }
            }
            break
          }

          case 'Reload Decision': {
            // Handle reloaded decision (e.g., from duplication)
            const { decisionId, parentDecisionFolderId } = action.meta

            // Only handle if the decision belongs to this folder
            if (parentDecisionFolderId === decisionFolderId) {
              // Check if this decision is already in our list
              const existingIndex = localChildren.findIndex(child => child.id === decisionId)

              if (existingIndex === -1) {
                // Decision not in list, fetch and add it
                fetchDecisionLite(accountId, decisionId).then(decisionData => {
                  if (decisionData) {
                    setLocalChildren(prev => [...prev, decisionData])
                  }
                })
              } else {
                // Decision exists, refresh its data
                fetchDecisionLite(accountId, decisionId).then(decisionData => {
                  if (decisionData) {
                    setLocalChildren(prev => prev.map(child => (child.id === decisionId ? decisionData : child)))
                  }
                })
              }
            }
            break
          }
        }
      },
      [childAncestry, decisionFolderId, refetch, accountId, localChildren]
    ),
  })

  // Use local state for children
  const decisionFolderChildNodes = localChildren

  const columns = useMemo<Column<any>[]>(
    () => [
      {
        id: 'name',
        Header: 'Name',
        accessor: 'name',
        Cell: cell => {
          const entity = cell.row.original
          switch (entity.entityType) {
            case 'Decision': {
              return <Link to={`/${accountCommonId}/decision/${entity.id}`}>{entity.name}</Link>
            }
            case 'DecisionFolder': {
              return (
                <Link to={`/${accountCommonId}/decision?folder=${entity.id}`}>
                  <FolderFilled /> {entity.name}
                </Link>
              )
            }
            default: {
              throw new Error('invalid entity type')
            }
          }
        },
      },
      {
        Header: 'Type',
        accessor: 'entityType',
        Cell: cell => {
          const { entityType, decisionType } = cell.row.original
          switch (entityType) {
            case 'Decision':
              switch (decisionType) {
                case 'Regular':
                  return <span>Decision</span>
                default:
                  // VM decision were the OG...and there may be decisions still floating
                  // around without a type property
                  return <span>VM Decision</span>
              }
            case 'DecisionFolder':
              return <span>Folder</span>
            default:
              return null
          }
        },
      },
      {
        Header: 'Description',
        accessor: 'description',
        disableSortBy: true,
        Cell: TableCellRenderer.Html as any,
      },
      {
        Header: 'Options',
        accessor: 'optionsCount',
        Cell: cell => {
          switch (cell.row.original.entityType) {
            case 'Decision':
              return cell.value
            case 'DecisionFolder':
              return null
          }
        },
        sortType: 'numeric',
        align: 'right',
      },
    ],
    [accountCommonId]
  )

  const dispatch = useAppDispatch()

  const { modal, showModal, hideModal } = useModalState()

  const showDecisionNodeCreateModal = useCallback(
    (decisionNodeType: 'Decision' | 'DecisionFolder') =>
      showModal(
        <DecisionNodeCreateModal hide={hideModal} ancestry={childAncestry} decisionNodeType={decisionNodeType} />
      ),
    [showModal, hideModal, childAncestry]
  )

  const createDecisionModal = useModalState()

  const showCreateDecisionModal = useCallback(
    () =>
      createDecisionModal.showModal(
        <CreateDecisionModal ancestry={childAncestry} hide={createDecisionModal.hideModal} />
      ),
    [childAncestry, createDecisionModal]
  )

  const duplicateDecisionModal = useModalState()

  const showDuplicateDecisionModal = useCallback(
    (decisionId: string, decisionName: string) =>
      duplicateDecisionModal.showModal(
        <DuplicateDecisionModal
          decisionId={decisionId}
          decisionName={decisionName}
          initialFolderId={directoryData.folder.id}
          hideModal={duplicateDecisionModal.hideModal}
        />
      ),
    [directoryData.folder.id, duplicateDecisionModal]
  )

  const moveDecisionModal = useModalState()

  const showMoveDecisionModal = useCallback(
    (decisionId: string, decisionName: string) => {
      moveDecisionModal.showModal(
        <MoveDecisionModal
          decisionId={decisionId}
          decisionName={decisionName}
          srcFolderId={directoryData.folder.id}
          hideModal={moveDecisionModal.hideModal}
        />
      )
    },
    [moveDecisionModal, directoryData]
  )

  return (
    <>
      {modal}
      {createDecisionModal.modal}
      {duplicateDecisionModal.modal}
      {moveDecisionModal.modal}
      <div
        style={{
          width: '100%',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
        }}
      >
        {accessLevel === 'Administrator' && (
          <>
            <Button
              {...testId('folder')}
              key="DecisionFolder"
              icon={<FolderAddOutlined />}
              onClick={() => showDecisionNodeCreateModal('DecisionFolder')}
              type="primary"
            >
              Create Folder
            </Button>
            <Button
              {...testId('decision')}
              key="Decision"
              icon={<PlusOutlined />}
              onClick={showCreateDecisionModal}
              type="primary"
            >
              Create Decision
            </Button>
          </>
        )}
      </div>
      <Table<any>
        columns={columns}
        data={decisionFolderChildNodes}
        rowControls={decisionNode =>
          accessLevel === 'Administrator' ? (
            <>
              {decisionNode.entityType === 'Decision' && (
                <>
                  <Tooltip title="Duplicate">
                    <FontAwesomeIcon
                      aria-label="Duplicate"
                      icon={faClone as any}
                      onClick={() => showDuplicateDecisionModal(decisionNode.id, decisionNode.name)}
                    />
                  </Tooltip>
                  <Tooltip title="Move">
                    <FontAwesomeIcon
                      aria-label="Move"
                      icon={faArrowsAlt as any}
                      onClick={() => showMoveDecisionModal(decisionNode.id, decisionNode.name)}
                    />
                  </Tooltip>
                </>
              )}
              <Tooltip title="Edit">
                <FontAwesomeIcon
                  aria-label="Edit"
                  icon={faEdit as any}
                  onClick={() =>
                    decisionNode.entityType === 'DecisionFolder'
                      ? showModal(<DecisionFolderEditModal decisionFolder={decisionNode} hide={hideModal} />)
                      : showModal(<DecisionEditModal decision={decisionNode} hide={hideModal} />)
                  }
                />
              </Tooltip>
              <Tooltip title="Delete">
                <FontAwesomeIcon
                  aria-label="Delete"
                  icon={faTrashAlt as any}
                  onClick={() =>
                    decisionNode.entityType === 'DecisionFolder'
                      ? showModal(<DecisionFolderDeleteModal decisionFolder={decisionNode} hide={hideModal} />)
                      : Modal.confirm({
                          title: `Deleting ${decisionNode.name}`,
                          content: `Are you sure you want to delete ${decisionNode.name}?`,
                          onOk: () => {
                            dispatch(deleteDecision(decisionNode.id, decisionFolderId))
                          },
                        })
                  }
                />
              </Tooltip>
            </>
          ) : (
            <></>
          )
        }
        initialState={{
          sortBy: [{ id: 'name', desc: false }],
        }}
      />
    </>
  )
}
